<template>
  <v-row no-gutters align="center">
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t('admin.inspections.inspections') }}
        </v-col>
        <v-col cols="auto" class="pr-2 text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="dialogAction('add')"
            :tooltip="$i18n.t('admin.inspections.addInspection')"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-7" align="center">
        <v-col cols="12">
          <ribbon-list
            :listItems="inspectionsArray"
            item-title-key="mainLayerVerboseName"
            no-items-translation-path="admin.inspections.noInspections"
            style-type="rawHigher"
            :toggleable="false"
          >
            <template #itemButtons-replace="{ item }">
              <dots-menu :items="inspectionDotsItems" v-on="getInspectionDotsListeners(item)" />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template #main_layer.input="data">
        <v-col cols="6">
          <data-input
            v-bind="data"
            v-model="mainInputDialogInputs.find(input => input.name === 'main_layer').value"
            @change="setCurrentLayerAttributes({ value: $event, type: 'main' })"
          />
        </v-col>
      </template>
      <template #relation_layer.input="data">
        <v-col cols="6">
          <data-input
            v-bind="data"
            v-model="mainInputDialogInputs.find(input => input.name === 'relation_layer').value"
            @change="setCurrentLayerAttributes({ value: $event, type: 'relation' })"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'InspectionsSettingsList',
  components: {
    DataInput,
    DotsMenu,
    RibbonList,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    layersNamesArray() {
      return this.$_objectToArray(this.layers || [])
        .filter(layer => {
          return layer.layer_scope === 'core' && layer.type === 'features_layer';
        })
        .map(layer => {
          return { value: layer.id, text: layer.name };
        });
    },
    nonGeomLayersNamesArray() {
      return this.$_objectToArray(this.layers || [])
        .filter(layer => {
          return layer.layer_scope === 'core' && layer.type === 'features_layer' && !layer.geometry_type;
        })
        .map(layer => {
          return { value: layer.id, text: layer.name };
        });
    },
    inspections: get('inspections/inspections'),
    inspectionsArray() {
      return this.$_objectToArray(this.inspections).map(inspection => {
        return {
          ...inspection,
          mainLayerVerboseName: this.layers[inspection.main_layer]?.name || '',
        };
      });
    },

    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },

    currentMainLayerIntegerAttributes() {
      return this.currentMainLayerAttributes?.filter(attribute => attribute.dataType?.name === 'integer') || [];
    },
    currentRelationLayerDateAttributes() {
      return this.currentRelationLayerAttributes?.filter(attribute => attribute.dataType?.name === 'date') || [];
    },
    currentRelationLayerIntegerAttributes() {
      return this.currentRelationLayerAttributes?.filter(attribute => attribute.dataType?.name === 'integer') || [];
    },

    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'MainInputDialog',
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'MainInputDialog',
          initAction: this.setCurrentLayers,
        },
      };
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.inspections.actions.${this.currentAction}InspectionTitle`),
              body: this.$i18n.t(`admin.inspections.actions.confirmInspection${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.mainLayerVerboseName,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.inspections.actions.${this.currentAction}InspectionTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'main_layer',
          translationPath: 'admin.inspections.attributes.mainLayer',
          subtitle: this.$i18n.t('admin.inspections.attributes.mainLayerSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.layersNamesArray,
        },
        {
          name: 'interval_attribute',
          translationPath: 'admin.inspections.attributes.interval',
          subtitle: this.$i18n.t('admin.inspections.attributes.intervalSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentMainLayerIntegerAttributes,
          disabled: !this.currentMainLayerAttributes,
        },
        {
          name: 'deadline_attribute',
          translationPath: 'admin.inspections.attributes.deadline',
          subtitle: this.$i18n.t('admin.inspections.attributes.deadlineSubtitle'),
          cols: '12',
          dataType: { name: 'select' },
          items: this.currentMainLayerIntegerAttributes,
          disabled: !this.currentMainLayerAttributes,
          clearable: true,
        },
        {
          name: 'relation_layer',
          translationPath: 'admin.inspections.attributes.relationLayer',
          subtitle: this.$i18n.t('admin.inspections.attributes.relationLayerSubtitle'),
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.nonGeomLayersNamesArray,
        },
        {
          name: 'relation_attribute',
          translationPath: 'admin.inspections.attributes.relationAttribute',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentRelationLayerIntegerAttributes,
          disabled: !this.currentRelationLayerAttributes,
        },
        {
          name: 'relation_date',
          translationPath: 'admin.inspections.attributes.relationDateAttribute',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentRelationLayerDateAttributes,
          disabled: !this.currentRelationLayerAttributes,
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs.map(input => {
          if (input.name === 'main_layer' || input.name === 'relation_layer') {
            return { ...input, disabled: true }; //?
          }
          return input;
        }),
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return {
          ...input,
          value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || null)),
        };
      });
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,

    currentMainLayer: '',
    currentMainLayerAttributes: undefined,
    currentRelationLayer: '',
    currentRelationLayerAttributes: undefined,

    inspectionDotsItems: [{ name: 'edit' }, { name: 'delete' }],

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
  }),
  methods: {
    addInspection: call('inspections/addInspection'),
    deleteInspection: call('inspections/deleteInspection'),
    editInspection: call('inspections/editInspection'),

    getInspectionDotsListeners(item) {
      return this.inspectionDotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, item);
        };
        return total;
      }, {});
    },

    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) initAction({ type, value });
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}Inspection`](data);
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerDeleteInspection() {
      await this.deleteInspection(this.currentActionData.id);
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerAddInspection() {
      await this.addInspection(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'));
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerEditInspection() {
      await this.editInspection({
        id: this.currentActionData.id,
        payload: this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'),
      });
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },

    setCurrentLayers({ value, types = ['main', 'relation'] } = {}) {
      types.forEach(type => {
        this.mainInputDialogInputs[`${this.$_capitalize(type)}_layer`] = value[`${type}_layer`];
        this.setCurrentLayerAttributes({ value: value[`${type}_layer`], type });
      });
    },
    setCurrentLayerAttributes({ value, type = 'main' } = {}) {
      const currentValues = JSON.parse(
        JSON.stringify(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'))
      );
      this[`current${this.$_capitalize(type)}LayerAttributes`] = (
        this.$_flattenStructure(
          this.getForm(
            this.layers[value]?.form_schema?.elements || [],
            this.dataSources[this.layers[value]?.data_source_name]?.attributes_schema?.attributes || []
          )
        ) || []
      )
        .filter(attribute => !attribute.readOnly && attribute.editable)
        .map(attribute => {
          return { value: attribute.attribute, text: attribute.label, dataType: attribute.dataType };
        });
      setTimeout(() => {
        Object.keys(currentValues).forEach(key => {
          const foundInput = this.mainInputDialogInputs.find(input => input.name === key);
          if (foundInput) foundInput.value = currentValues[key];
        });
      });
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.currentMainLayerAttributes = undefined;
          this.currentRealtionLayerAttributes = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
  },
};
</script>
